import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { 
  LuxurySearch as Search, 
  LuxuryStar as Star, 
  LuxuryChevronRight as ChevronRight, 
  LuxuryMapPin as MapPin, 
  LuxuryUsers as Users,
  LuxuryBed as Bed,
  LuxuryBath as Bath,
  LuxuryHome as Home,
  LuxuryGlobe as Globe,
  LuxuryAward as Award
} from "./icons/LuxuryIcons";
import EmotionalFilters from "./EmotionalFilters";
import ConciergeChat from "./ConciergeChat";
import DestinationsSection from "./DestinationsSection";
import { useTranslation } from "../hooks/useTranslation";

const HomePageV2 = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // States
  const [featuredVillas, setFeaturedVillas] = useState([]);
  const [newVillas, setNewVillas] = useState([]);
  const [bestValueVillas, setBestValueVillas] = useState([]);
  const [topDestinations, setTopDestinations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEmotion, setSelectedEmotion] = useState(null);

  // Load data from API
  useEffect(() => {
    loadHomePageData();
  }, []);

  const loadHomePageData = async () => {
    try {
      setLoading(true);

      // MODE TEST : IDs fixes pour chaque section
      const PRESTIGE_IDS = [8742, 8084, 9193, 7756, 5647, 4846, 9616, 9715]; // 8 villas prestige
      const NEW_IDS = [8009, 5657, 7865, 2352, 6166, 8742]; // 6 nouvelles villas (IDs valides)
      const BEST_VALUE_IDS = [2352, 6166, 8009, 5657, 7865, 8084]; // 6 coups de cœur (IDs valides)

      // Fonction helper pour récupérer des villas par IDs
      const fetchVillasByIds = async (ids) => {
        const villaPromises = ids.map(id => 
          axios.get(`/api/villas/${id}`).catch(err => {
            console.warn(`Villa ${id} non trouvée:`, err.message);
            return null;
          })
        );
        const results = await Promise.all(villaPromises);
        return results
          .filter(r => r && r.data && r.data.success && r.data.villa)
          .map(r => {
            const villa = r.data.villa;
            const photos = r.data.photos || [];
            const rates = r.data.rates || [];
            
            return {
              id: villa.id,
              public_name: villa.public_name,
              name: villa.public_name,
              zone: villa.zone,
              city: villa.city,
              country: villa.country,
              number_bedrooms: villa.number_bedrooms,
              number_bathrooms: villa.number_bathrooms,
              number_pax: villa.number_pax,
              capacity: villa.number_pax,
              bedrooms: villa.number_bedrooms,
              bathrooms: villa.number_bathrooms,
              villa_area: villa.villa_area,
              type_of_house: villa.type_of_house,
              main_photo: photos.length > 0 ? photos[0].photo_url : null,
              min_price: rates.length > 0 ? Math.min(...rates.map(rate => parseFloat(rate.price || 0))) : 0,
              photos_count: photos.length
            };
          });
      };

      // Fetch villas par section
      const [prestigeVillas, recentVillas, bestValueVillas] = await Promise.all([
        fetchVillasByIds(PRESTIGE_IDS),
        fetchVillasByIds(NEW_IDS),
        fetchVillasByIds(BEST_VALUE_IDS)
      ]);

      // Fetch destinations
      const destinationsResponse = await axios.get('/api/destinations');

      // Fetch stats
      const statsResponse = await axios.get('/api/villas/stats');

      setFeaturedVillas(prestigeVillas);
      setNewVillas(recentVillas);
      setBestValueVillas(bestValueVillas);
      setTopDestinations(destinationsResponse.data.destinations?.slice(0, 6) || []);
      setStats(statsResponse.data);

      console.log('📊 Villas chargées (MODE TEST - IDs fixes):', {
        prestige: prestigeVillas.length,
        nouvelles: recentVillas.length,
        coupDeCoeur: bestValueVillas.length
      });

    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  };

  const VillaCardLuxury = ({ villa }) => {
    const mainPhoto = villa.main_photo || 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800';
    const priceDisplay = (villa.min_price && parseFloat(villa.min_price) > 0) 
      ? `${Math.round(villa.min_price).toLocaleString('fr-FR')} €` 
      : 'Sur demande';

    return (
      <div 
        onClick={() => navigate(`/villa/${villa.id}`)}
        className="group cursor-pointer bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
      >
        {/* Image */}
        <div className="relative h-72 overflow-hidden">
          <img 
            src={mainPhoto}
            alt={villa.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800';
            }}
          />
          
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          {/* Type badge */}
          {villa.type_of_house && (
            <Badge className="absolute top-4 left-4 bg-white/90 text-gray-900 backdrop-blur-sm border-0">
              {villa.type_of_house}
            </Badge>
          )}

          {/* Quick view button */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <Button className="bg-white text-gray-900 hover:bg-amber-500 hover:text-white transform scale-90 group-hover:scale-100 transition-transform">
              <span className="mr-2">Découvrir</span>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Location */}
          <div className="flex items-center text-sm text-gray-600 mb-2">
            <MapPin className="w-4 h-4 mr-1 text-amber-600" />
            <span className="font-light">{villa.zone || villa.country}</span>
          </div>

          {/* Name */}
          <h3 
            className="text-xl font-light mb-3 text-gray-900 line-clamp-1 group-hover:text-amber-600 transition-colors"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            {villa.name}
          </h3>

          {/* Features */}
          <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
            {villa.capacity && (
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                <span>{villa.capacity}</span>
              </div>
            )}
            {villa.bedrooms && (
              <div className="flex items-center gap-1">
                <Bed className="w-4 h-4" />
                <span>{villa.bedrooms}</span>
              </div>
            )}
            {villa.bathrooms && (
              <div className="flex items-center gap-1">
                <Bath className="w-4 h-4" />
                <span>{villa.bathrooms}</span>
              </div>
            )}
          </div>

          {/* Price */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <div>
              <p className="text-sm text-gray-500 font-light">À partir de</p>
              <p className="text-xl font-medium text-gray-900">{priceDisplay}</p>
              {villa.min_price && parseFloat(villa.min_price) > 0 && (
                <p className="text-xs text-gray-400">par nuit</p>
              )}
            </div>
            <ChevronRight className="w-5 h-5 text-amber-600 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="bg-gradient-to-br from-slate-50 via-white to-blue-50">
        {/* Hero Section - Full height */}
        <section className="relative h-screen flex items-center justify-center overflow-hidden">
          {/* Background Video YouTube */}
          <div className="absolute inset-0 overflow-hidden">
            <iframe
              className="absolute"
              src="https://www.youtube.com/embed/pCt4wPt6zH0?autoplay=1&mute=1&loop=1&playlist=pCt4wPt6zH0&controls=0&showinfo=0&rel=0&iv_load_policy=3&modestbranding=1&disablekb=1&fs=0&cc_load_policy=0&playsinline=1&enablejsapi=0"
              title="PRIVIA Villas - Luxury Background Video"
              frameBorder="0"
              allow="autoplay; encrypted-media"
              allowFullScreen={false}
              style={{
                width: '177.77777778vh',
                height: '56.25vw',
                minWidth: '100%',
                minHeight: '100%',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                border: 'none'
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60" />
          </div>

          {/* Hero Content */}
          <div className="relative z-10 text-center text-white max-w-5xl mx-auto px-6">
            <Badge 
              variant="secondary" 
              className="mb-6 px-6 py-3 bg-white/10 backdrop-blur-md border-white/20 text-white text-sm font-light uppercase tracking-[0.3em]"
            >
              Collection Exclusive de Villas de Prestige
            </Badge>
            
            <h1 
              className="text-6xl md:text-8xl font-light mb-8 tracking-wide leading-tight"
              style={{ 
                fontFamily: 'Playfair Display, serif',
                textShadow: '0 4px 30px rgba(212, 175, 55, 0.4)'
              }}
            >
              Vivez l'Excellence
            </h1>
            
            <p 
              className="text-2xl md:text-3xl font-light mb-6 opacity-95 max-w-3xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Des villas d'exception dans les destinations les plus prestigieuses du monde
            </p>
            
            <p className="text-lg mb-12 opacity-85 italic max-w-2xl mx-auto">
              Service de conciergerie 24/7 • Sélection rigoureuse • Expériences sur mesure
            </p>

            {/* Stats Bar */}
            {stats && (
              <div className="flex flex-wrap justify-center gap-8 mb-12">
                <div className="text-center">
                  <div className="text-4xl font-light mb-1">{stats.total_villas}+</div>
                  <p className="text-sm text-white/80 uppercase tracking-wider">Villas de luxe</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-light mb-1">{stats.total_countries}+</div>
                  <p className="text-sm text-white/80 uppercase tracking-wider">Pays</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-light mb-1">98%</div>
                  <p className="text-sm text-white/80 uppercase tracking-wider">Satisfaction</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-light mb-1">24/7</div>
                  <p className="text-sm text-white/80 uppercase tracking-wider">Conciergerie</p>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-10 py-6 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl"
                onClick={() => navigate('/search')}
              >
                Explorer nos Villas
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
              <div className="w-1 h-2 bg-white/60 rounded-full"></div>
            </div>
          </div>
        </section>

        {/* Destinations Section */}
        <div className="bg-gradient-to-br from-slate-50 via-white to-blue-50">
          <DestinationsSection />
        </div>

        {/* Featured Villas Section - "Sélection Exclusive" */}
        <section className="py-24 px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <Badge className="mb-4 px-4 py-2 bg-amber-50 text-amber-800 border-amber-200">
                <Award className="w-4 h-4 inline mr-2" />
                Sélection Prestige
              </Badge>
              <h2 
                className="text-5xl md:text-6xl font-light mb-6 text-gray-900"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Collection Exclusive
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
                Nos villas les plus exceptionnelles, sélectionnées pour leur caractère unique,
                leur emplacement privilégié et leur niveau de prestations incomparable
              </p>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-72 bg-gray-200 rounded-t-lg"></div>
                    <div className="p-6 bg-white rounded-b-lg">
                      <div className="h-4 bg-gray-200 rounded mb-4"></div>
                      <div className="h-6 bg-gray-200 rounded mb-4"></div>
                      <div className="h-4 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {featuredVillas.map((villa) => (
                  <VillaCardLuxury key={villa.id} villa={villa} />
                ))}
              </div>
            )}

            <div className="text-center mt-12">
              <Button 
                variant="outline"
                size="lg"
                className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white px-8 py-4 rounded-full font-medium transition-all duration-300"
                onClick={() => navigate('/search')}
              >
                Voir toute la collection
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </section>

        {/* New Villas Section - "Nouveautés" */}
        <section className="py-24 px-6 bg-gradient-to-br from-amber-50 via-white to-blue-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <Badge className="mb-4 px-4 py-2 bg-blue-50 text-blue-800 border-blue-200">
                <Star className="w-4 h-4 inline mr-2" filled />
                Nouveautés
              </Badge>
              <h2 
                className="text-5xl md:text-6xl font-light mb-6 text-gray-900"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Dernières Acquisitions
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
                Découvrez en avant-première les dernières perles ajoutées à notre collection
              </p>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-72 bg-gray-200 rounded-t-lg"></div>
                    <div className="p-6 bg-white rounded-b-lg">
                      <div className="h-4 bg-gray-200 rounded mb-4"></div>
                      <div className="h-6 bg-gray-200 rounded mb-4"></div>
                      <div className="h-4 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {newVillas.map((villa) => (
                  <VillaCardLuxury key={villa.id} villa={villa} />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Best Value Section - "Coups de Cœur" */}
        <section className="py-24 px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <Badge className="mb-4 px-4 py-2 bg-rose-50 text-rose-800 border-rose-200">
                <Star className="w-4 h-4 inline mr-2" filled />
                Coups de Cœur
              </Badge>
              <h2 
                className="text-5xl md:text-6xl font-light mb-6 text-gray-900"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Nos Recommandations
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
                Une sélection de villas offrant un rapport qualité/prestations exceptionnel, 
                parfaites pour des séjours inoubliables en famille ou entre amis
              </p>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-72 bg-gray-200 rounded-t-lg"></div>
                    <div className="p-6 bg-white rounded-b-lg">
                      <div className="h-4 bg-gray-200 rounded mb-4"></div>
                      <div className="h-6 bg-gray-200 rounded mb-4"></div>
                      <div className="h-4 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {bestValueVillas.map((villa) => (
                  <VillaCardLuxury key={villa.id} villa={villa} />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Top Destinations Section */}
        <section className="py-24 px-6 bg-gradient-to-br from-slate-50 via-white to-blue-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <Badge className="mb-4 px-4 py-2 bg-green-50 text-green-800 border-green-200">
                <Globe className="w-4 h-4 inline mr-2" />
                Destinations
              </Badge>
              <h2 
                className="text-5xl md:text-6xl font-light mb-6 text-gray-900"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Destinations Phares
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
                Les régions les plus prisées de notre clientèle internationale
              </p>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-64 bg-gray-200 rounded-lg animate-pulse"></div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {topDestinations.map((destination) => (
                  <div 
                    key={destination.country}
                    onClick={() => navigate(`/search?country=${encodeURIComponent(destination.country)}`)}
                    className="group relative h-80 rounded-lg overflow-hidden cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-500"
                  >
                    <img 
                      src={`https://source.unsplash.com/800x600/?luxury,villa,${destination.country}`}
                      alt={destination.country}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                      <h3 
                        className="text-3xl font-light mb-2"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        {destination.country}
                      </h3>
                      <p className="text-white/90 font-light flex items-center">
                        <Home className="w-4 h-4 mr-2" />
                        {destination.count} villas de prestige
                      </p>
                      <div className="mt-4 flex items-center text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="mr-2">Explorer</span>
                        <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Trust & Excellence Section */}
        <section className="py-20 px-6 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
          <div className="max-w-6xl mx-auto text-center">
            <h2 
              className="text-4xl md:text-5xl font-light mb-8"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              L'Excellence PRIVIA
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mt-16">
              <div>
                <Award className="w-12 h-12 mx-auto mb-4 text-amber-400" />
                <h3 className="text-xl font-light mb-3">Sélection Rigoureuse</h3>
                <p className="text-gray-300 font-light leading-relaxed">
                  Chaque villa est minutieusement inspectée et évaluée selon nos critères d'excellence
                </p>
              </div>
              <div>
                <Users className="w-12 h-12 mx-auto mb-4 text-amber-400" />
                <h3 className="text-xl font-light mb-3">Conciergerie 24/7</h3>
                <p className="text-gray-300 font-light leading-relaxed">
                  Une équipe dédiée disponible à tout moment pour répondre à vos besoins
                </p>
              </div>
              <div>
                <Star className="w-12 h-12 mx-auto mb-4 text-amber-400" filled />
                <h3 className="text-xl font-light mb-3">Expériences Sur Mesure</h3>
                <p className="text-gray-300 font-light leading-relaxed">
                  Services personnalisés et prestations exclusives pour un séjour inoubliable
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-6 bg-gradient-to-br from-amber-50 via-white to-blue-50">
          <div className="max-w-4xl mx-auto text-center">
            <h2 
              className="text-4xl md:text-5xl font-light mb-6 text-gray-900"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Prêt à Vivre l'Extraordinaire ?
            </h2>
            <p className="text-xl text-gray-600 mb-10 font-light leading-relaxed">
              Laissez nos experts vous guider vers la villa parfaite pour vos prochaines vacances
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-10 py-6 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105 shadow-xl"
                onClick={() => navigate('/search')}
              >
                Découvrir nos Villas
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                variant="outline"
                size="lg"
                className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white px-10 py-6 rounded-full font-medium text-lg transition-all duration-300"
                onClick={() => navigate('/contact')}
              >
                Contactez-nous
              </Button>
            </div>
          </div>
        </section>
      </div>

      {/* Concierge Chat Widget */}
      <ConciergeChat />
    </>
  );
};

export default HomePageV2;

